import {
  APIRequestLog,
  LinkedIntegration,
  LinkedIntegrationWebhookLog,
  IntegrationSettingChoice,
  LinkedIntegrationAPIUsageMetric,
  LinkedIntegrationHealth,
  LinkedModelHealth,
  LinkedModel,
} from '../models';
import dayjs from 'dayjs';
import { ModelResource } from './resource';
import { APIRequestLogQueryParams, PaginatedQueryParams } from '@api/query';

export class LinkedIntegrations extends ModelResource<LinkedIntegration> {
  model = LinkedIntegration;

  resourcePath = 'v1/linked-integrations';

  async getAPILogs(
    linkedIntegrationId: string,
    params?: APIRequestLogQueryParams,
  ): Promise<{ totalRecords: number; data: APIRequestLog[] }> {
    const response = await this.api.get(this.path(linkedIntegrationId, 'logs'), {
      params: params?.serialize({ forApiRequest: true }),
    });

    return {
      totalRecords: response.data.total_records,
      data: response.data.data.map((log) => APIRequestLog.fromJson(log)),
    };
  }

  async getAuthorizationUrl(linkedIntegrationId: string, redirectURI: string): Promise<string> {
    return this.api
      .post(this.path(linkedIntegrationId, 'oauth', 'authorization'), {
        data: { redirect_uri: redirectURI },
      })
      .then((res) => {
        return res.data.url as string;
      });
  }

  async getAPIUsageMetrics(
    linkedIntegrationId: string,
    params?: { since?: dayjs.Dayjs; timezone?: string },
  ): Promise<LinkedIntegrationAPIUsageMetric[]> {
    return this.api
      .get(this.path(linkedIntegrationId, 'usage-metrics'), {
        params,
      })
      .then((response) => {
        return response.data.data.map((l) => LinkedIntegrationAPIUsageMetric.fromJson(l));
      });
  }

  async getWebhookLogs(
    linkedIntegration: LinkedIntegration,
    params?: PaginatedQueryParams,
  ): Promise<{ totalRecords: number; data: LinkedIntegrationWebhookLog[] }> {
    const response = await this.api.get(this.path(linkedIntegration.id, 'webhook-logs'), {
      params: params?.serialize({ forApiRequest: true }),
    });

    const logs = response.data.data.map((log) => {
      const parsedLog = LinkedIntegrationWebhookLog.fromJson(log);
      parsedLog.linked_integration = linkedIntegration;
      return parsedLog;
    });

    return { totalRecords: response.data.total_records, data: logs };
  }

  async getSettingChoices(
    linkedIntegration: LinkedIntegration,
    setting: string,
  ): Promise<IntegrationSettingChoice[]> {
    const settings = await this.api.get(
      this.path(linkedIntegration.id, 'settings', setting, 'choices'),
    );

    return settings.data.data.map((s) => IntegrationSettingChoice.fromJson(s));
  }

  async requestSync(
    linkedIntegrationId: string,
    linkedIntegration?: LinkedIntegration,
  ): Promise<LinkedIntegration> {
    return this.api.post(this.path(linkedIntegrationId, 'sync')).then((li) => {
      if (linkedIntegration) {
        linkedIntegration.updateFromJson(li);
        return linkedIntegration;
      }

      return LinkedIntegration.fromJson(li);
    });
  }

  async checkHealth(linkedIntegrationId: string): Promise<LinkedIntegrationHealth> {
    const response = await this.api.post(this.path(linkedIntegrationId, 'check-health'));
    return response.data;
  }

  async checkLinkedModelHealth(
    linkedIntegrationId: string,
    linkedModelId: string,
  ): Promise<LinkedModelHealth> {
    const response = await this.api.post(
      this.path(linkedIntegrationId, 'models', linkedModelId, 'check-health'),
    );
    return response.data;
  }

  async requestFileSync(linkedModel: LinkedModel, fileId: string): Promise<LinkedModel> {
    const response = await this.api.post(
      this.path(linkedModel.linked_integration_id, 'models', linkedModel.id, 'file-sync'),
      { data: { file: fileId } },
    );
    linkedModel.updateFromJson(response.data);

    return linkedModel;
  }
}
